/**
 * Detect whether a keyboard event originated from a text-editable surface.
 *
 * Intended as the shared guard for global keyboard-shortcut handlers
 * that need to defer to text editing. Most global handlers in the
 * portal use it (Ctrl+Z toolbar handler, Ctrl+S save, Ctrl+F canvas
 * search, copy/paste/cut, command palette, canvas keyboard navigation,
 * ?-help dialog, Ctrl+Shift+1/2 tab switching, AnalyticsPage 'r'/'e',
 * EnvironmentVariablesTab Ctrl+S, Tab add-node panel). New global
 * handlers should also use it — but verify rather than assume universal
 * coverage, especially for shortcuts where the desired behaviour
 * inside editors diverges from the default (e.g. `useCommandPalette`
 * intentionally allows Cmd+K from plain inputs and only skips code
 * editors).
 *
 * Industry consensus (n8n, Figma, Notion, Linear, ComfyTavern, Power
 * Automate) is a HARD BOUNDARY: when focus is inside any editable
 * surface, app-level shortcuts MUST NOT fire, even when the surface's
 * own undo stack is empty. Falling through would silently revert the
 * canvas/workflow — a catastrophic UX surprise.
 *
 * The check uses `Element.closest(...)` rather than `instanceof` because
 * CodeMirror, Lexical, ProseMirror, and similar editors dispatch keydown
 * events from descendant nodes (e.g. `.cm-line`, `<span>` inside a
 * contenteditable root) whose `tagName` is not INPUT/TEXTAREA and whose
 * `isContentEditable` is `false`.
 */

/**
 * Canonical selector list for "the user is typing here".
 *
 * Order is for readability — `closest()` doesn't care.
 *
 *  - `input`, `textarea`, `select`               — native form controls
 *  - `[contenteditable]:not([contenteditable="false"])` — covers
 *      contenteditable, contenteditable="", contenteditable="true"
 *      (HTML spec treats all three as editable; only "false" disables it)
 *  - `.cm-editor`                                — CodeMirror 6
 *  - `.monaco-editor`                            — Monaco
 *  - `[data-lexical-editor]`                     — Lexical (HtmlEditor)
 *  - `[role="textbox|combobox|spinbutton|searchbox"]`
 *                                                — Fluent UI v9 composite editors
 *  - `[data-no-canvas-shortcuts]`                — explicit opt-out hatch
 */
export const EDITABLE_TARGET_SELECTOR = [
  'input',
  'textarea',
  'select',
  '[contenteditable]:not([contenteditable="false"])',
  '.cm-editor',
  '.monaco-editor',
  '[data-lexical-editor]',
  '[role="textbox"]',
  '[role="combobox"]',
  '[role="spinbutton"]',
  '[role="searchbox"]',
  '[data-no-canvas-shortcuts]',
].join(', ');

/**
 * Returns true when `target` is, or is a descendant of, a text-editable
 * surface (native input, contenteditable, CodeMirror, Monaco, Lexical,
 * or a Fluent v9 composite editor role).
 *
 * Guards against non-Element targets (e.g. `document` itself, which has
 * no `.closest()`).
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  if (!el) return false;
  return el.closest(EDITABLE_TARGET_SELECTOR) !== null;
}

/**
 * Convenience guard for global `keydown` handlers that want to bail out
 * cleanly whenever the user is typing.
 *
 * Returns true (caller should `return` without `preventDefault`) when:
 *  - the user is mid-IME-composition (`e.isComposing`)
 *  - the event target is an editable surface
 *
 * Never call `e.preventDefault()` after this returns true — doing so
 * would also block the browser's native text behavior (e.g. text undo).
 *
 * Note: `e.repeat` is intentionally NOT handled here. A handler that
 * owns a shortcut should still call `preventDefault()` on key-repeat
 * events to suppress the browser default, then bail to avoid
 * re-firing its action. The recommended pattern is:
 *
 *     if (shouldSkipShortcut(e)) return;
 *     // We own this key — suppress browser default even on repeats.
 *     e.preventDefault();
 *     if (e.repeat) return;
 *     doAction();
 */
export function shouldSkipShortcut(e: KeyboardEvent): boolean {
  if (e.isComposing) return true;
  return isEditableTarget(e.target);
}
